import React, { useCallback, useMemo } from 'react';
import { Button, Row } from 'antd';
import { get, groupBy } from 'lodash-es';
import { useRouter } from 'views/useRouter';

import { classnames } from 'lib/classnames';
import { AUTOMATE_NODES, AUTOMATE_TYPE } from 'lib/constants/automation';
import { useConstantConfig } from 'lib/hooks';
import useI18n from 'lib/hooks/useI18n';
import { AutomationNodeList, ComponentInterface, NodeDataInterface } from 'lib/types/automation';

import NodeContainer from './common/NodeContainer';
import SwitchElement from './SwitchElement';

import styles from './SwitchComponent.module.less';

const cx = classnames(styles);

const SwitchComponent: React.FC<ComponentInterface> = props => {
  const { clickNode, onUpdateNode, storageNode } = props;
  const { t } = useI18n();
  const { query } = useRouter();

  const { AUTO_NODE_LIST_FOR_CREATE, SWITCH_COMPONENT_LIST, SYSTEM_AUTO_NODE_LIST } = useConstantConfig();
  const typeGroup = useMemo(() => groupBy(SWITCH_COMPONENT_LIST, 'type'), []);

  const groupByComponent = useMemo(() => {
    const autoNodes =
      query?.isSystem === 'true' ? [...AUTO_NODE_LIST_FOR_CREATE, ...SYSTEM_AUTO_NODE_LIST] : AUTO_NODE_LIST_FOR_CREATE;
    return groupBy(autoNodes, 'type');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query?.isSystem]);

  const changeDetails = useCallback(
    component => {
      const { type, view } = component;
      if (view === AUTOMATE_NODES.CONDITION_IF_ELSE) {
        onUpdateNode({ ...clickNode, type, view, name: 'If', conditionType: 'if' });
      } else {
        onUpdateNode({ ...clickNode, type, view, name: component.title });
      }
    },
    [clickNode, onUpdateNode],
  );

  const backToDefault = useCallback(() => {
    onUpdateNode(storageNode);
  }, [storageNode, onUpdateNode]);

  const componentData = useMemo(() => {
    // 需要标记当前类型是节点的list
    const list = (groupByComponent[clickNode.type] as AutomationNodeList[])?.filter(node => !node.hide) || [];
    const { title, desc } = get(typeGroup[clickNode.type], '[0]', {}) as unknown as NodeDataInterface;
    return {
      list,
      title,
      desc,
    };
  }, [clickNode, groupByComponent]);

  // if/else节点只在第一层节点可选
  const componentList = useMemo(() => {
    const allComponent = componentData.list;
    return clickNode.path.length > 1
      ? allComponent?.filter(({ view }) => view !== AUTOMATE_NODES.CONDITION_IF_ELSE)
      : allComponent;
  }, [clickNode.path.length, componentData.list]);

  return (
    <NodeContainer title={componentData.title} desc={componentData.desc}>
      <div className={cx('component-list')}>
        {componentList.map(component => {
          return <SwitchElement onClickNode={changeDetails} element={component} key={component?.view} />;
        })}
      </div>
      {clickNode?.type !== AUTOMATE_TYPE.TRIGGER && (
        <Row justify="end">
          <Button onClick={backToDefault}>{t('global.cancel')}</Button>
        </Row>
      )}
    </NodeContainer>
  );
};

export default SwitchComponent;
